/*
 * @file Wrapper functions for https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 */

import CookieUtils, { CookieName } from 'chairisher/util/cookie';

/**
 * An exception that is thrown when a fetch function receives an unexpected response.
 */
export class FetchError extends Error {
    constructor(options) {
        const { method, status, statusText, url } = options;
        super(`${method} ${url} returned ${status} ${statusText}`);
        this.options = options;
        this.json = options.json;
    }

    /**
     * @returns {String|undefined} The first error message in the json response (not always present).
     */
    get firstErrorMessage() {
        return this.options?.json?.errors?.[0]?.message;
    }
}

const jsonType = 'application/json';

const chairishFetchHeaders = {
    'x-requested-with': 'XMLHttpRequest', // See the python is_ajax function.
};

const jsonHeaders = {
    'Accept': jsonType,
    'Content-Type': jsonType,
    ...chairishFetchHeaders,
};

function getCsrfHeader() {
    return { 'X-CSRFToken': CookieUtils.getValueFromCookie(CookieName.CSRF_TOKEN) };
}

export async function chairishFetch(url, options) {
    const response = await fetch(url, options);
    let json = null;

    if (response.headers.get('Content-Type')?.includes(jsonType)) {
        try {
            json = await response.json();
        } catch (e) {
            throw new FetchError({
                json,
                status: response.status,
                statusText: `json parsing error: ${e.message}`,
                url,
                ...options,
            });
        }
    }

    if (response.ok) {
        return json;
    }

    throw new FetchError({
        json,
        status: response.status,
        statusText: response.statusText,
        url,
        ...options,
    });
}

/**
 * Sends a GET request to the given url.
 *
 * @param url {String}
 * @return {Promise<Object>}
 * @throws {FetchError}
 */
export function getJSON(url) {
    return chairishFetch(url, {
        method: 'GET',
        headers: jsonHeaders,
    });
}

/**
 * Sends a POST request with a json body to the given url.
 *
 * @param {String} url
 * @param {Object=} body Object to json stringify and set as the request body
 * @return {Promise<Object>}
 * @throws {FetchError}
 */
export function postJSON(url, body = {}) {
    return chairishFetch(url, {
        method: 'POST',
        headers: { ...getCsrfHeader(), ...jsonHeaders },
        body: JSON.stringify(body),
    });
}

/**
 * Sends a PUT request with a json body to the given url.
 *
 * @param {String} url
 * @param {Object=} body Object to json stringify and set as the request body
 * @return {Promise<Object>}
 * @throws {FetchError}
 */
export function putJSON(url, body = {}) {
    return chairishFetch(url, {
        method: 'PUT',
        headers: { ...getCsrfHeader(), ...jsonHeaders },
        body: JSON.stringify(body),
    });
}

/**
 * Sends a DELETE request to the given url.
 *
 * @param {String} url
 * @return {Promise<Object>}
 * @throws {FetchError}
 */
export function deleteJSON(url) {
    return chairishFetch(url, {
        method: 'DELETE',
        headers: { ...getCsrfHeader(), ...jsonHeaders },
    });
}

/**
 * Submits the given html form asynchronously with fetch.
 *
 * @param {HTMLFormElement} form
 * @param {Object<string, string>=} extraData
 * @return {Promise<Object>}
 * @throws {FetchError}
 */
export function submitForm(form, extraData = {}) {
    const body = new FormData(form);
    Object.entries(extraData).forEach(([key, val]) => {
        if (Array.isArray(val)) {
            val.forEach((item) => {
                body.append(key, item);
            });
        } else {
            body.set(key, val);
        }
    });
    return chairishFetch(form.action, {
        body,
        headers: { ...getCsrfHeader(), ...chairishFetchHeaders },
        method: form.method,
    });
}
